import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { useMessagingStore } from '@/store/messaging.store';
import { useUpdateConversation, useAddParticipant, useRemoveParticipant, useDeleteConversation } from '@/hooks/useMessages';
import type { ConversationWithPreview, ParticipantRole, MessageWithDetails } from '@/types/messaging';
import { API_URL } from '@/services/api';
import { toast } from '../common/Toast';

import Avatar from '../common/Avatar';
import Button, { IconButton } from '../common/Button';
import TabButton from '../common/TabButton';
import Input from '../common/Input';
import FaIcon from '../common/FaIcon';
import Modal from '../common/Modal';

interface ConversationSettingsProps {
  conversation: ConversationWithPreview;
  onClose: () => void;
  onLeaveGroup: () => void;
}

interface User {
  id: string;
  name: string;
  email: string;
}

export function ConversationSettings({ conversation, onClose, onLeaveGroup }: ConversationSettingsProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'members' | 'add'>('info');
  const [groupName, setGroupName] = useState(conversation.name || '');
  const [groupDescription, setGroupDescription] = useState(conversation.description || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Leaving a group, and removing someone from one, both take a person out of
  // an operational thread they cannot put themselves back into. Same two-step
  // as Delete Group below.
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'json' | 'txt'>('txt');

  const { token, user } = useAuthStore();
  const { activeTenant } = useTenantStore();
  const { messagesByConversation, mutedConversations, toggleMuteConversation } = useMessagingStore();
  const updateConversation = useUpdateConversation();
  const addParticipant = useAddParticipant();
  const removeParticipant = useRemoveParticipant();
  const deleteConversation = useDeleteConversation();

  // Get current user's role
  const currentUserParticipant = conversation.participants?.find(p => p.user_id === user?.id);
  const isOwner = currentUserParticipant?.role === 'owner';
  const isAdmin = currentUserParticipant?.role === 'admin' || isOwner;
  const canEdit = conversation.type !== 'direct' && isAdmin;

  // Fetch users for adding
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users-for-messaging', activeTenant?.id, searchQuery],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/tenants/${activeTenant?.id}/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': activeTenant?.id || '',
        },
        params: { search: searchQuery },
      });
      return response.data as { data: User[] };
    },
    enabled: !!activeTenant?.id && activeTab === 'add',
  });

  // Filter out existing participants
  const existingUserIds = new Set(conversation.participants?.map(p => p.user_id) || []);
  const availableUsers = usersData?.data?.filter(u => !existingUserIds.has(u.id)) || [];

  const handleUpdateInfo = async () => {
    if (!canEdit) return;
    try {
      await updateConversation.mutateAsync({
        conversationId: conversation.id,
        input: {
          name: groupName,
          description: groupDescription,
        },
      });
    } catch (error) {
      console.error('Failed to update conversation:', error);
      toast.error('Failed to update conversation');
    }
  };

  const handleAddMember = async (userId: string) => {
    try {
      await addParticipant.mutateAsync({
        conversationId: conversation.id,
        userId,
      });
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to add member:', error);
      toast.error('Failed to add member');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await removeParticipant.mutateAsync({
        conversationId: conversation.id,
        userId,
      });
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error('Failed to remove member');
    }
  };

  const handleLeaveGroup = async () => {
    if (!user) return;
    try {
      await removeParticipant.mutateAsync({
        conversationId: conversation.id,
        userId: user.id,
      });
      onLeaveGroup();
    } catch (error) {
      console.error('Failed to leave group:', error);
      toast.error('Failed to leave group');
    }
  };

  const handleDeleteGroup = async () => {
    if (!isOwner) return;
    try {
      await deleteConversation.mutateAsync(conversation.id);
      onLeaveGroup();
    } catch (error) {
      console.error('Failed to delete group:', error);
      toast.error('Failed to delete group');
    }
  };

  const getRoleBadge = (role: ParticipantRole) => {
    switch (role) {
      case 'owner':
        return <span className="px-1.5 py-0.5 text-xs bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 rounded">Owner</span>;
      case 'admin':
        return <span className="px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-gray-700 text-primary-700 dark:text-primary-300 rounded">Admin</span>;
      default:
        return null;
    }
  };

  // Export chat history
  const handleExportChat = useCallback(async () => {
    setIsExporting(true);
    try {
      const messages = messagesByConversation[conversation.id] || [];
      const conversationName = conversation.type === 'direct' && conversation.other_participant
        ? conversation.other_participant.name
        : conversation.name || 'Conversation';

      let content: string;
      let filename: string;
      let mimeType: string;

      if (exportFormat === 'json') {
        // Export as JSON
        const exportData = {
          conversation: {
            id: conversation.id,
            name: conversationName,
            type: conversation.type,
            created_at: conversation.created_at,
            participant_count: conversation.participant_count,
          },
          messages: messages.map((m: MessageWithDetails) => ({
            id: m.id,
            sender: m.sender?.name || 'Unknown',
            content: m.content,
            type: m.type,
            created_at: m.created_at,
            edited_at: m.edited_at,
            reactions: m.reactions,
          })),
          exported_at: new Date().toISOString(),
          total_messages: messages.length,
        };
        content = JSON.stringify(exportData, null, 2);
        filename = `chat-export-${conversationName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
      } else {
        // Export as TXT
        const lines: string[] = [
          `Chat Export: ${conversationName}`,
          `Type: ${conversation.type}`,
          `Exported: ${new Date().toLocaleString()}`,
          `Total Messages: ${messages.length}`,
          '',
          '='.repeat(50),
          '',
        ];

        for (const message of messages) {
          const timestamp = new Date(message.created_at).toLocaleString();
          const sender = message.sender?.name || 'Unknown';
          const editedNote = message.edited_at ? ' (edited)' : '';

          lines.push(`[${timestamp}] ${sender}${editedNote}`);
          lines.push(message.content || '');

          if (message.reactions && message.reactions.length > 0) {
            const reactionSummary = message.reactions
              .map((r) => `${r.emoji}`)
              .join(' ');
            lines.push(`Reactions: ${reactionSummary}`);
          }
          lines.push('');
        }

        content = lines.join('\n');
        filename = `chat-export-${conversationName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.txt`;
        mimeType = 'text/plain';
      }

      // Create and download file
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export chat:', error);
      toast.error('Failed to export chat');
    } finally {
      setIsExporting(false);
    }
  }, [conversation, messagesByConversation, exportFormat]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={conversation.type === 'direct' ? 'Conversation Info' : 'Group Settings'}
      size="md"
    >

        {/* Tabs (only for groups) */}
        {conversation.type !== 'direct' && (
          <div className="flex border-b border-theme-primary">
            <TabButton active={activeTab === 'info'} onClick={() => setActiveTab('info')} className="flex-1 justify-center">
              Info
            </TabButton>
            <TabButton active={activeTab === 'members'} onClick={() => setActiveTab('members')} className="flex-1 justify-center">
              Members ({conversation.participant_count || conversation.participants?.length || 0})
            </TabButton>
            {canEdit && (
              <TabButton active={activeTab === 'add'} onClick={() => setActiveTab('add')} className="flex-1 justify-center">
                Add
              </TabButton>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div className="p-4 space-y-4">
              {/* Avatar */}
              <div className="flex justify-center">
                <div
                  className={`w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-2xl ${
                    conversation.type === 'direct'
                      ? 'bg-primary-500'
                      : conversation.type === 'group'
                      ? 'bg-purple-500'
                      : 'bg-orange-500'
                  }`}
                >
                  {conversation.type === 'direct' && conversation.other_participant
                    ? conversation.other_participant.name.charAt(0).toUpperCase()
                    : conversation.type === 'incident'
                    ? '!'
                    : '#'}
                </div>
              </div>

              {/* Group Name */}
              {conversation.type !== 'direct' ? (
                <div>
                  <label htmlFor="group-name" className="block text-sm font-medium text-theme-secondary mb-1">
                    Group Name
                  </label>
                  <Input id="group-name"
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    disabled={!canEdit}
                    placeholder="Group name"
                  />
                </div>
              ) : (
                <div className="text-center">
                  <h3 className="text-lg font-medium text-theme-primary">
                    {conversation.other_participant?.name}
                  </h3>
                  <p className="text-sm text-theme-tertiary">
                    {conversation.other_participant?.email}
                  </p>
                </div>
              )}

              {/* Description */}
              {conversation.type !== 'direct' && (
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-theme-secondary mb-1">
                    Description
                  </label>
                  <Input id="description"
                    as="textarea"
                    value={groupDescription}
                    onChange={(e) => setGroupDescription(e.target.value)}
                    disabled={!canEdit}
                    rows={3}
                    className="resize-none"
                    placeholder="Group description (optional)"
                  />
                </div>
              )}

              {/* Save button */}
              {canEdit && (groupName !== conversation.name || groupDescription !== conversation.description) && (
                <Button
                  variant="primary"
                  size="sm"
                  fullWidth
                  onClick={handleUpdateInfo}
                  disabled={updateConversation.isPending}
                  loading={updateConversation.isPending}
                >
                  {updateConversation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              )}

              {/* Type badge */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  conversation.type === 'direct'
                    ? 'bg-primary-100 dark:bg-gray-700 text-primary-700 dark:text-primary-300'
                    : conversation.type === 'group'
                    ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300'
                    : 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300'
                }`}>
                  {conversation.type === 'direct' ? 'Direct Message' : conversation.type === 'group' ? 'Group Chat' : 'Incident Thread'}
                </span>
                <span className="text-xs text-theme-tertiary">
                  Created {new Date(conversation.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* Export Chat History */}
              <div className="pt-4 border-t border-theme-primary">
                <label className="block text-sm font-medium text-theme-secondary mb-2">
                  Export Chat History
                </label>
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => setExportFormat('txt')}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      exportFormat === 'txt'
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-tertiary text-theme-secondary hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    Text (.txt)
                  </button>
                  <button
                    onClick={() => setExportFormat('json')}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      exportFormat === 'json'
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface-tertiary text-theme-secondary hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    JSON (.json)
                  </button>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  onClick={handleExportChat}
                  disabled={isExporting}
                  loading={isExporting}
                  icon={!isExporting ? <FaIcon icon="download" className="w-4 h-4" /> : undefined}
                >
                  {isExporting ? 'Exporting...' : 'Download Chat History'}
                </Button>
                <p className="text-xs text-theme-tertiary mt-1 text-center">
                  {(messagesByConversation[conversation.id] || []).length} messages will be exported
                </p>
              </div>

              {/* Mute Conversation */}
              <div className="pt-4 border-t border-theme-primary">
                <button
                  onClick={() => toggleMuteConversation(conversation.id)}
                  className="w-full flex items-center justify-between py-2 px-3 bg-surface-tertiary rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {mutedConversations.has(conversation.id) ? (
                      <FaIcon icon="volume-xmark" className="w-5 h-5 text-theme-tertiary" />
                    ) : (
                      <FaIcon icon="volume-high" className="w-5 h-5 text-theme-tertiary" />
                    )}
                    <span className="text-sm font-medium text-theme-secondary">
                      {mutedConversations.has(conversation.id) ? 'Unmute Notifications' : 'Mute Notifications'}
                    </span>
                  </div>
                  <div
                    className={`w-10 h-6 rounded-full p-1 transition-colors ${
                      mutedConversations.has(conversation.id) ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        mutedConversations.has(conversation.id) ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </div>
                </button>
                <p className="text-xs text-theme-tertiary mt-1 text-center">
                  {mutedConversations.has(conversation.id)
                    ? 'You will not receive notifications for new messages'
                    : 'Turn off notifications for this conversation'}
                </p>
              </div>
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="py-2">
              {conversation.participants?.map((participant) => (
                <div
                  key={participant.user_id}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <Avatar src={undefined} name={participant.user?.name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-theme-primary truncate">
                        {participant.user?.name}
                        {participant.user_id === user?.id && (
                          <span className="text-theme-tertiary"> (you)</span>
                        )}
                      </p>
                      {getRoleBadge(participant.role)}
                    </div>
                    <p className="text-xs text-theme-tertiary truncate">
                      {participant.user?.email}
                    </p>
                  </div>
                  {/* Remove button */}
                  {canEdit && participant.user_id !== user?.id && participant.role !== 'owner' && (
                    confirmRemoveId === participant.user_id ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-xs text-theme-tertiary">Remove?</span>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={removeParticipant.isPending}
                        onClick={() => {
                          setConfirmRemoveId(null);
                          handleRemoveMember(participant.user_id);
                        }}
                      >
                        Remove
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmRemoveId(null)}>
                        Cancel
                      </Button>
                    </div>
                    ) : (
                    <IconButton
                      icon={<FaIcon icon="xmark" className="w-4 h-4" />}
                      label="Remove member"
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmRemoveId(participant.user_id)}
                      className="text-gray-400 hover:text-red-500"
                    />
                    )
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add Members Tab */}
          {activeTab === 'add' && (
            <div className="p-4 space-y-3">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users to add..."
                aria-label="Search users to add"
              />

              <div className="space-y-1">
                {usersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                ) : availableUsers.length === 0 ? (
                  <p className="text-center text-theme-tertiary py-8 text-sm">
                    {searchQuery ? 'No users found' : 'All users are already members'}
                  </p>
                ) : (
                  availableUsers.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => handleAddMember(u.id)}
                      disabled={addParticipant.isPending}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Avatar src={undefined} name={u.name} size="sm" />
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-theme-primary truncate">{u.name}</p>
                        <p className="text-xs text-theme-tertiary truncate">{u.email}</p>
                      </div>
                      <FaIcon icon="plus" className="w-5 h-5 text-primary-500" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {conversation.type !== 'direct' && (
          <div className="px-4 py-3 border-t border-theme-primary space-y-2">
            {!isOwner && (
              confirmLeave ? (
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" className="flex-1" onClick={() => setConfirmLeave(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="flex-1"
                    onClick={handleLeaveGroup}
                    disabled={removeParticipant.isPending}
                    loading={removeParticipant.isPending}
                  >
                    Confirm Leave
                  </Button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  fullWidth
                  onClick={() => setConfirmLeave(true)}
                  className="text-red-600 dark:text-red-400"
                >
                  Leave Group
                </Button>
              )
            )}
            {isOwner && (
              confirmDelete ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                    onClick={() => setConfirmDelete(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="flex-1"
                    onClick={handleDeleteGroup}
                    disabled={deleteConversation.isPending}
                    loading={deleteConversation.isPending}
                  >
                    {deleteConversation.isPending ? 'Deleting...' : 'Confirm Delete'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="danger"
                  size="sm"
                  fullWidth
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete Group
                </Button>
              )
            )}
          </div>
        )}
    </Modal>
  );
}
